import React, { useEffect, useState } from 'react';
import {
  Modal,
  Row,
  Col,
  Typography,
  DatePicker,
  Checkbox,
  message,
  ModalProps,
  Divider,
} from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { useMutation, queryCache } from 'react-query';
import { patchTendersRegistrationInfo } from '~src/services/api';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import ModuleTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';

interface QaSettingsModalProps
  extends Pick<ModalProps, 'visible' | 'onOk' | 'onCancel'> {
  projectId?: string;
  tenderId?: string;
  tenderData: any;
}

const format = 'DD/MM/YYYY - HH:mm';

function parseDate(date: any) {
  if (!date) return null;
  const parsed = typeof date === 'number' ? moment.unix(date) : moment(date);
  return parsed.isValid() ? parsed : null;
}

// Helper function copied from DatesModalWindow to handle time disabling logic
function getDisabledTime(
  min?: moment.Moment | null,
  max?: moment.Moment | null
) {
  return (current: moment.Moment | null) => {
    if (!current) return {};

    const curDate = current.clone();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    let disabledHours: number[] = [];
    let disabledMinutes: ((hour: number) => number[]) | undefined;

    // Logic for Minimum Time (e.g., Start Date)
    if (min && curDate.isSame(min, 'day')) {
      const minHour = min.hour();
      const minMinute = min.minute();

      disabledHours = hours.filter((h) => h < minHour);

      disabledMinutes = (hour: number) =>
        hour === minHour ? minutes.filter((m) => m < minMinute) : [];
    }

    // Logic for Maximum Time (e.g., End Date)
    if (max && curDate.isSame(max, 'day')) {
      const maxHour = max.hour();
      const maxMinute = max.minute();

      disabledHours = [...disabledHours, ...hours.filter((h) => h > maxHour)];

      const prevDisabled = disabledMinutes;
      disabledMinutes = (hour: number) => {
        const prev = prevDisabled?.(hour) ?? [];
        const moreThan =
          hour === maxHour ? minutes.filter((m) => m > maxMinute) : [];
        return [...new Set([...prev, ...moreThan])];
      };
    }

    return {
      disabledHours: () => Array.from(new Set(disabledHours)),
      disabledMinutes: disabledMinutes ?? (() => []),
    };
  };
}

export const QaSettingsModal: React.FC<QaSettingsModalProps> = ({
  visible,
  onOk,
  onCancel,
  projectId,
  tenderId,
  tenderData,
}) => {
  const intl = useIntl();
  const [qa_on, setQa_on] = useState<boolean>(false);
  const [qa_start, setQa_start] = useState<moment.Moment | null>(null);
  const [qa_stop, setQa_stop] = useState<moment.Moment | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Destructure needed values directly from tenderData
  const { title, reference, deadline_at } = tenderData || {};
  const deadline = parseDate(deadline_at);

  useEffect(() => {
    if (visible && tenderData) {
      setQa_on(!!tenderData.qa_on);
      setQa_start(parseDate(tenderData.qa_start));
      setQa_stop(parseDate(tenderData.qa_stop));
    }
  }, [visible, tenderData]);

  const [doSave] = useMutation(patchTendersRegistrationInfo, {
    onMutate: () => setIsModalLoading(true),
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);
      onOk?.(null as any);
    },
  });

  const handleSave = async () => {
    if (!projectId || !tenderId) {
      return message.error(intl.formatMessage({ id: 'qa-error-missing-ids' }));
    }

    if (qa_on) {
      if (qa_start && qa_stop && qa_stop.isSameOrBefore(qa_start, 'minute')) {
        return message.error(
          intl.formatMessage({ id: 'qa-error-stop-before-start' })
        );
      }
      if (deadline && qa_stop && qa_stop.isAfter(deadline, 'minute')) {
        return message.error(
          intl.formatMessage({ id: 'qa-error-stop-after-deadline' })
        );
      }
    }

    await doSave({
      projectId,
      tenderId,
      qa_on,
      qa_start: qa_on && qa_start ? qa_start.unix() : null,
      qa_stop: qa_on && qa_stop ? qa_stop.unix() : null,
    });
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <NavIcon
            customStyle={{ width: '35px', height: '20px' }}
            icon={ModuleTenderHammerNoColor}
          />
          <span style={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: 'tender-details-edit-dates' })} (
            {intl.formatMessage({ id: 'qa-acronym' })})
          </span>
        </div>
      }
      visible={visible}
      onOk={handleSave}
      onCancel={onCancel}
      confirmLoading={isModalLoading}
      okText={intl.formatMessage({ id: 'save' })}
      destroyOnClose
      width={480}>
      <div style={{ marginBottom: 20 }}>
        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          {intl.formatMessage({ id: 'tender' })}
        </Typography.Text>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title || reference}
        </Typography.Title>
      </div>

      <div
        style={{
          background: '#FFFBE6',
          border: '1px solid #FFE58F',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: 20,
        }}>
        <Typography.Text
          strong
          style={{
            fontSize: '11px',
            color: '#856404',
            textTransform: 'uppercase',
          }}>
          {intl.formatMessage({ id: 'tender-details-deadline' })} (
          {intl.formatMessage({ id: 'reference-only' })})
        </Typography.Text>
        <div style={{ fontSize: '15px', fontWeight: 600 }}>
          {deadline
            ? deadline.format(format)
            : intl.formatMessage({ id: 'not-available' })}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '6px',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography.Text strong>
            {intl.formatMessage({ id: 'tender-qa' })}{' '}
            {intl.formatMessage({ id: 'features' })}
          </Typography.Text>
          <IconPopup
            messageId={'tender_edit_dates_help_qa_message'}
            headerId={'tender_edit_dates_help_qa_header'}
          />
        </div>
        <Checkbox
          checked={qa_on}
          onChange={(e) => {
            setQa_on(e.target.checked);
            if (!e.target.checked) {
              setQa_start(null);
              setQa_stop(null);
            }
          }}
        />
      </div>

      {qa_on && (
        <>
          <Divider style={{ margin: '20px 0' }} />
          <Row gutter={20}>
            <Col span={12}>
              <label
                style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {intl.formatMessage({ id: 'tender-details-qa_start' })}
              </label>
              <DatePicker
                style={{ width: '100%' }}
                showTime
                showNow={false}
                format={format}
                value={qa_start}
                onChange={setQa_start}
                // DISABLE DATES: Cannot be after QA Stop (if set) OR after Deadline
                disabledDate={(date) => {
                  if (!date) return false;
                  // If we have a stop date, Start cannot be AFTER it
                  if (qa_stop && date.isAfter(qa_stop, 'day')) return true;
                  // Start cannot be AFTER the hard deadline
                  if (deadline && date.isAfter(deadline, 'day')) return true;
                  return false;
                }}
                // DISABLE TIME: If same day as Stop, block later hours
                disabledTime={getDisabledTime(null, qa_stop || deadline)}
              />
            </Col>
            <Col span={12}>
              <label
                style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {intl.formatMessage({ id: 'tender-details-qa_stop' })}
              </label>
              <DatePicker
                style={{ width: '100%' }}
                showTime
                showNow={false}
                format={format}
                value={qa_stop}
                onChange={setQa_stop}
                // DISABLE DATES: Cannot be before QA Start (if set) OR after Deadline
                disabledDate={(date) => {
                  if (!date) return false;
                  // Cannot be before Start
                  if (qa_start && date.isBefore(qa_start, 'day')) return true;
                  // Cannot be after Deadline
                  if (deadline && date.isAfter(deadline, 'day')) return true;
                  return false;
                }}
                // DISABLE TIME: If same day as Start, block earlier hours
                disabledTime={getDisabledTime(qa_start, deadline)}
              />
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
};
